import React from "react";
import Table, { Column } from "../../../../components/ui/dashboard/Table";
import { formatCurrencyForTable, formatDateDDMMYYYY } from "../../../../lib/utils";
import { DispatchImportIssue } from "../../@types";

export interface ImportStat {
  label: string;
  value: React.ReactNode;
  tone?: "default" | "success" | "warning" | "danger";
}

const toneStyles: Record<NonNullable<ImportStat["tone"]>, string> = {
  default: "text-hColor",
  success: "text-green-700",
  warning: "text-yellow-700",
  danger: "text-red-600",
};

export const ImportStatsGrid: React.FC<{ stats: ImportStat[] }> = ({
  stats,
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
    {stats.map((stat) => (
      <div
        key={stat.label}
        className="border border-gray-200 rounded-lg p-3 bg-white"
      >
        <p className="text-[12px] text-pColor">{stat.label}</p>
        <p
          className={`text-[18px] font-semibold ${
            toneStyles[stat.tone || "default"]
          }`}
        >
          {stat.value}
        </p>
      </div>
    ))}
  </div>
);

export const ImportSection: React.FC<{
  title: string;
  count?: number;
  children: React.ReactNode;
}> = ({ title, count, children }) => (
  <div className="space-y-3">
    <h3 className="text-[15px] font-semibold text-hColor">
      {title}
      {count !== undefined && (
        <span className="ml-2 text-[12px] font-medium text-pColor bg-gray-100 rounded-full px-2 py-0.5">
          {count}
        </span>
      )}
    </h3>
    {children}
  </div>
);

export const ImportIssuesTable: React.FC<{
  issues: DispatchImportIssue[];
  tone: "danger" | "warning";
}> = ({ issues, tone }) => {
  const columns: Column<DispatchImportIssue>[] = [
    {
      key: "sourceRow",
      header: "Row",
      render: (value: number) => (
        <span className="text-hColor">{value || "-"}</span>
      ),
    },
    {
      key: "column",
      header: "Column",
      render: (value: string) => (
        <span className="text-hColor">{value || "-"}</span>
      ),
    },
    {
      key: "message",
      header: "Message",
      render: (value: string) => (
        <span className={toneStyles[tone]}>{value}</span>
      ),
    },
  ];

  return <Table data={issues} columns={columns} />;
};

export const formatImportDate = (value?: string | null) =>
  value ? formatDateDDMMYYYY(value) : "-";

export const formatImportAmount = (value?: number) =>
  formatCurrencyForTable(value || 0);

export const formatDateRange = (from?: string, to?: string) => {
  if (!from && !to) return "-";
  if (!to || from === to) return formatImportDate(from || to);
  return `${formatImportDate(from)} to ${formatImportDate(to)}`;
};

export const ImportStatusBadge: React.FC<{ status?: string }> = ({
  status,
}) => {
  const normalized = (status || "").toLowerCase();
  const style = normalized.includes("revers")
    ? "text-gray-700 bg-gray-100 border-gray-300"
    : normalized.includes("fail")
    ? "text-red-700 bg-red-100 border-red-300"
    : normalized.includes("partial") || normalized.includes("warn")
    ? "text-yellow-700 bg-yellow-100 border-yellow-300"
    : "text-green-700 bg-green-100 border-green-300";

  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-[12px] font-medium border whitespace-nowrap ${style}`}
    >
      {status || "Unknown"}
    </span>
  );
};
